/**
 * split-copy: what a Split does to the Objective and the Note (L9, 2026-09-25).
 *
 * A Split copies the quote's envelope into both halves. The Objective and the
 * Note describe the quote's items, so a copy made as it was written can
 * describe items that are now on the other quote: live, the split-off Roof
 * quote still read "... to carry out balcony: replace cracked floor tiles ...,
 * balcony: waterproof ..., and roof: replace 4 m of corroded flashing ...".
 *
 * The rule:
 *   - When the Objective the quote shows is the one QuoteMaker derives from its
 *     items (deriveRjBirdObjectiveFromScope), each half gets the Objective
 *     derived from its own items.
 *   - Any other Objective (written by the operator, imported with the quote,
 *     or wording QuoteMaker can no longer reproduce) is kept as written on both
 *     quotes, and both quotes carry a flag telling the operator to check it.
 *   - The Note is never rewritten. When it names an item of the quote, both
 *     quotes carry a flag telling the operator to check it.
 *   - A derived Objective longer than the server accepts (SPLIT_OBJECTIVE_MAX;
 *     it has one clause per item, so a large quote gets there) is treated like
 *     any other: kept as written and flagged, so the Objective never makes the
 *     split fail.
 *
 * Planned in the browser because the derivation is frontend code and the
 * backend must not import it. The server partitions items from its own stored
 * copy and accepts these values only as whitelisted, type-checked fields
 * (backend/src/services/quote-split.js), and it refuses to replace an
 * Objective the stored quote marks as the operator's.
 */

import { normaliseQuotePunctuation } from '../../utils/scope-descriptions';
import { deriveRjBirdObjectiveFromScope } from '../quote-objective';

/**
 * The longest derived Objective the server accepts for one half, in
 * characters after trimming. Mirrors SPLIT_OBJECTIVE_MAX in
 * backend/src/services/quote-split.js (the backend must not import frontend
 * code); src/__tests__/quote-jobs-split-copy.test.ts pins the two equal.
 */
export const SPLIT_OBJECTIVE_MAX = 4000;

export interface SplitCopyReview {
    /** Both quotes keep an Objective QuoteMaker did not derive: check it on both. */
    objective: boolean;
    /** The Note names items of the quote: check it on both. */
    note: boolean;
}

export interface SplitCopyPlan {
    /** Each half's Objective, or null to keep the stored one as written. */
    objective: { source: string; target: string } | null;
    review: SplitCopyReview;
}

export interface SplitCopyInput {
    /** The quote's items, as the editor derives the Objective from them. */
    items: unknown[];
    /**
     * Other copies of the same items the stored Objective may have been
     * derived from: the stored Stage 5 items before display clean-up, and the
     * Stage 4 scope quote generation derives from. Tried in order after items;
     * the first whose derivation matches is split and derived again.
     */
    otherItemSets?: unknown[][];
    /** The ids of the items moving to the new quote. */
    itemIds: string[];
    /** The Objective the quote shows now (resolveQuoteObjective). */
    objective: unknown;
    /** formData.scopeObjectiveEditedByUser === true: the operator wrote it. */
    objectiveWrittenByOperator: boolean;
    /** The Note the quote shows now. */
    note: unknown;
}

/** The one line that tells the operator what to check after a split, or null. */
export function splitReviewMessage(review: Partial<SplitCopyReview> | null | undefined): string | null {
    if (review?.objective === true && review?.note === true) return 'Check the Objective and the Note on both quotes.';
    if (review?.objective === true) return 'Check the Objective on both quotes.';
    if (review?.note === true) return 'Check the Note on both quotes.';
    return null;
}

function clean(value: unknown): string {
    return normaliseQuotePunctuation(String(value ?? ''))
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function itemId(item: unknown): string {
    const id = item && typeof item === 'object' ? (item as { id?: unknown }).id : null;
    return typeof id === 'string' ? id.trim() : '';
}

function itemTitle(item: unknown): string {
    return clean(item && typeof item === 'object' ? (item as { title?: unknown }).title : '');
}

/** Lower case, letters and digits only, single spaces: for whole-word matching. */
function words(value: unknown): string {
    return clean(value).toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
}

/** "Roof: replace flashing" or "Roof - Flashing replacement" -> the area and the work. */
function titleParts(title: string): { area: string; work: string } | null {
    const match = title.match(/^(.+?)(?:\s+-\s+|\s*:\s+)(.+)$/);
    return match ? { area: words(match[1]), work: words(match[2]) } : null;
}

function containsPhrase(haystack: string, phrase: string): boolean {
    return phrase.length > 0 && ` ${haystack} `.includes(` ${phrase} `);
}

/**
 * Does the Note name an item of the quote? It does when it carries an item's
 * title, the work part of a title of at least two words, an area that only
 * one half has, or an item number (both halves are renumbered).
 */
export function noteNamesQuoteItems(note: unknown, movedItems: unknown[], keptItems: unknown[]): boolean {
    const text = words(note);
    if (!text) return false;
    if (/\bitems?\s+(?:no\s+)?\d/.test(text)) return true;

    const areasOf = (items: unknown[]) => new Set(items
        .map(item => titleParts(itemTitle(item))?.area || '')
        .filter(Boolean));
    const movedAreas = areasOf(movedItems);
    const keptAreas = areasOf(keptItems);

    for (const item of [...movedItems, ...keptItems]) {
        const title = itemTitle(item);
        const full = words(title);
        if (full.length >= 4 && containsPhrase(text, full)) return true;
        const parts = titleParts(title);
        if (!parts) continue;
        if (parts.work.split(' ').length >= 2 && containsPhrase(text, parts.work)) return true;
        const onlyOneHalf = movedAreas.has(parts.area) !== keptAreas.has(parts.area);
        if (onlyOneHalf && parts.area.length >= 3 && containsPhrase(text, parts.area)) return true;
    }
    return false;
}

function objectItems(value: unknown): unknown[] {
    return Array.isArray(value) ? value.filter(item => item && typeof item === 'object') : [];
}

export function planSplitCopy(input: SplitCopyInput): SplitCopyPlan {
    const moving = new Set((input.itemIds || []).map(id => String(id).trim()));
    const partition = (items: unknown[]) => ({
        moved: items.filter(item => moving.has(itemId(item))),
        kept: items.filter(item => !moving.has(itemId(item))),
    });
    const items = objectItems(input.items);
    const shown = partition(items);

    const review: SplitCopyReview = {
        objective: false,
        note: noteNamesQuoteItems(input.note, shown.moved, shown.kept),
    };

    const objective = clean(input.objective);
    if (!objective) return { objective: null, review };

    // A set counts only when both halves keep an item of it, so neither half
    // is ever given the "works described below" wording of an empty list.
    const basis = input.objectiveWrittenByOperator
        ? null
        : [items, ...(input.otherItemSets || []).map(objectItems)].find((set) => {
            const halves = partition(set);
            return halves.moved.length > 0 && halves.kept.length > 0
                && objective === clean(deriveRjBirdObjectiveFromScope(set));
        });
    if (!basis) {
        review.objective = true;
        return { objective: null, review };
    }
    const halves = partition(basis);
    const source = deriveRjBirdObjectiveFromScope(halves.kept);
    const target = deriveRjBirdObjectiveFromScope(halves.moved);
    if (source.trim().length > SPLIT_OBJECTIVE_MAX || target.trim().length > SPLIT_OBJECTIVE_MAX) {
        review.objective = true;
        return { objective: null, review };
    }
    return { objective: { source, target }, review };
}

/**
 * The quote without its check-after-split flag (quote_data.meta.splitReview),
 * or the same object when it carries none. Used when the operator presses
 * Checked, in the editor and in App's copy of the quote.
 */
export function quoteWithoutSplitReview<T>(quoteData: T): T {
    const meta = quoteData && typeof quoteData === 'object'
        ? (quoteData as { meta?: unknown }).meta
        : null;
    if (!meta || typeof meta !== 'object' || !Object.prototype.hasOwnProperty.call(meta, 'splitReview')) {
        return quoteData;
    }
    const { splitReview: _checked, ...rest } = meta as Record<string, unknown>;
    return { ...(quoteData as object), meta: rest } as T;
}
